import { InlineTranslateable } from '@fmnts/i18n';
import { ConditionDefinition } from '../condition/conditions';
import { ValidatorConfig } from '../validation/validation.types';
import { FormNode } from './form-declaration-tree';

// ==================================================================
// Base interfaces for Fields, Fieldsets & Pages
// ==================================================================

/**
 * Base Interface for Fields
 */
export interface FieldDefinition extends FormNode {
  /**
   * Label for a field
   */
  label: InlineTranslateable | undefined;
  /**
   * Object storing all additional properties for a Field.
   *
   * @deprecated this was used to store all untyped properties,
   * however once we can be sure we have correct types and mappings
   * we can get rid of this value.
   */
  attrs?: Record<string, any> | undefined;
  /**
   * Conditions for a Field
   *
   * @description Conditions on Fields allow to hide a
   * field if the condition is not fullfilled.
   *
   * - `FieldCondition`, if conditional additional field.
   * - `string`, if widget is `'conditionalSelect'`.
   * - `null` or `undefined`, if field is not conditional.
   */
  condition?: ConditionDefinition | undefined;
  /**
   * Styles for a Field
   *
   * @description This applies classes to fields, e.g.
   * can use TailwindCSS style e.g. `w-full`
   */
  style?: string | undefined;
  /**
   * Widget type for Field
   *
   * Depending on the widget, a field can have different properties
   */
  widget: string | undefined;
  /**
   * Widget Options contains all relevant properties, for this type
   * of field.
   */
  widgetOptions: any;
  /**
   * The Type of the widget, can be one of:
   * * `'input'`
   * * `'textbox'`
   * * `'action'`
   */
  widgetType: any;
}

/**
 * Base Interface for a Fieldset
 */
export interface FieldsetDefinition extends FormNode {
  /**
   * Label for a Fieldset
   *
   * @description Labels are rendered at the beginning
   * of each Fieldset as a `<h2>`-HTMLElement
   */
  label: InlineTranslateable;
  /**
   * Conditions for Fieldsets
   *
   * @description Conditions on Fieldset allow to hide a whole
   * group of fields if the condition is not fullfilled.
   */
  condition?: ConditionDefinition | undefined;
  /**
   * List of fields
   *
   * Fieldsets are instantiated and rendered in the order they
   * are configured.
   */
  readonly fields: ReadonlyArray<FieldDefinition>;
}

/**
 * Base Interface for a Page
 */
export interface PageDefinition extends FormNode {
  /**
   * Label for a page
   *
   * @todo Currently there is no 'label' rendered for a page,
   * so maybe remove this property or show label
   */
  label: InlineTranslateable;
  /**
   * List of Fieldsets for page
   *
   * Fieldsets are instantiated and rendered in the order they
   * are configured.
   */
  readonly fieldsets: ReadonlyArray<FieldsetDefinition>;
  /**
   * List of Navigation Actions
   *
   * Actions are rendered as simple Buttons at the end of a page,
   * and can trigger pre-configured functions.
   */
  readonly navigation: ReadonlyArray<NavigationActionDefinition>;
  /**
   * List of Modals
   *
   * Based on the key, it can instantiate a Modal
   * @see {@link NotificationDefinition}
   */
  readonly modals: ReadonlyArray<NotificationDefinition>;
}

// ==================================================================
// Types for Fields
// ==================================================================

/**
 * Basic Interface for an Input Field
 */
export interface InputDefinition {
  /**
   * A list of validations
   *
   * @see ValidatorConfig
   */
  validators?: ValidatorConfig[];
  /**
   * Each input can have an initial value, which can be set statically or
   * dynamic value.
   * ### static values
   * these are set by using the value itself
   * @example
   * ```
   * widget: "date_summary",
   * value: "2024-07-05"
   * ```
   *
   * ### dynamic values
   * these are set by using a template syntax, and fetched from the
   * current values from the field-references
   * @example
   * ```
   * widget: "text_summary",
   * value: "{{first_name}} {{last_name}}"
   * ```
   *
   * @see DeclarativeFieldBuilder.getInitialFieldControlState
   */
  defaultValue?: unknown | undefined;
  /**
   * Scope for a Field
   *
   * @description scoped values are persisted in
   * formunauts-state.
   */
  scope: FieldScope | string | undefined;
}

/**
 * Templates are FormFields with the purpose of rendering content.
 *
 */
export interface TemplateDefinition {
  /**
   * Text contains either plain text or html
   *
   * Can also contain template expressions that are transformed by FormTemplateService
   * */
  text: InlineTranslateable;
  /**
   * @description With the implementation of quizzes, we started using
   * a dedicated API for assets. In order to use this API we store which
   * assets are actually used for this template, so they can be deleted
   * from the server, once they are deleted from the template-field.
   */
  assets?: unknown[];
  /**
   * Images can be appended, and are inserted to the right side of the textbox
   */
  image?: string | undefined;
}
/**
 * Action Elements
 *
 * Actions can be used to initiate function calls dynamically on the
 * form.
 */
export interface ActionDefinition {
  /**
   * Each action is associated by its `name`, which has to be
   * unique for each action.
   */
  action: string;
  /**
   * This is an action specific configuration.
   * This means that it must be interpreted in
   * combination with the action.
   */
  actionConfig: unknown;
}

// ==================================================================
// Types for Input Fields
// ==================================================================

/**
 * The Scope for which values should be stored.
 *
 * The initilization for input values is done within the FieldBuilder.
 * The persisting for each scope is handled in the actual form-component.
 *
 * @see DeclarativeFieldBuilder.getInitialFieldControlState
 */
export enum FieldScope {
  /**
   * Session Scope
   *
   * Persists values in the 'sessionValues` object.
   * These values will be stored for the current session
   * (until a user logs out).
   */
  Session = 'session',
  /**
   * Form Scope (default)
   *
   * Values are persisted while the form is open.
   */
  Form = 'form',
}

/**
 * Select Input Fields are different from basic Input fields,
 * since they have a set of valid values.
 */
export interface SelectInput {
  /**
   * A list of items to choose from
   */
  items: SelectInputItem[];
  /**
   * Similarly to how conditions are applied to fields, conditions on
   * items will change the visibility of the select-items.
   */
  itemConditions?: Record<string, ConditionDefinition>;
}

/**
 * Text Input Fields have properties to change
 * the behaviour of how text is inserted into the input field
 */
export interface TextInput {
  /**
   * This attribute controls the type of keyboard that should be used,
   * for mobile users and will set the property `inputMode` on HTMLInputElements.
   *
   * Currently handled values are `tel`, `number`, `email`
   * @default `text`
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
   */
  keyboardType?: string | undefined;
  /**
   * Type of the Input Field
   *
   * @default `text`
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types
   */
  format?: string | undefined;
  /**
   * This attribute affects virtual keyboards
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/autocapitalize
   */
  autocapitalize?: string | undefined;
}

/**
 * Represents an item for a select-like field, e.g.
 * * radio-groups
 * * checkboxes
 */
export interface SelectInputItem extends Record<string, any> {
  /**
   * Label for the item
   */
  label: InlineTranslateable;
  /**
   * Value of the item
   */
  value: any;
  /**
   * Descriptions can be used to show additional text, usually
   * rendered with a grey color
   */
  description?: InlineTranslateable | undefined;
  /**
   * Images can be used, instead of regular checkboxes, in order
   * to choose between an item
   *
   * @see CheckboxesWithContent
   */
  image?: string | undefined;
}

// ==================================================================
// Navigation
// ==================================================================
/**
 * Represents a navigation action.
 */
export interface NavigationActionDefinition {
  readonly action: string;
  readonly label: InlineTranslateable;
  readonly style: string | undefined;
  readonly condition: ConditionDefinition | undefined;
}

// ==================================================================
// Notifications
// ==================================================================
/**
 * Represents an action to be used in a notification.
 */
export interface NotificationActionDefinition {
  /** The unique name of the action. */
  readonly action: string;
  /** Action label. */
  readonly label: InlineTranslateable;
  /** Additional CSS classes to apply on the action. */
  readonly style: string;
}

/**
 * Represents a generic notification.
 */
export interface NotificationDefinition {
  /** Unique key for the modal. */
  readonly key: string;
  /** Modal buttons. */
  readonly buttons: readonly NotificationActionDefinition[];
  /** Title of the modal. */
  readonly title: InlineTranslateable;
  /** Modal body. */
  readonly text: InlineTranslateable;
  /** Optional image. */
  readonly image: InlineTranslateable | null;
}
